import React from 'react';
import { useTranslation } from 'react-i18next';

import { formatAmountWithCode, formatAmount } from '../../../../data/billing';
import { currencySymbol } from '../../../../lib/currency';
import type { SpeiTransferDetails } from '../../../../data/commerce-gate';
import { SPEI } from '../../spei.tokens';
import { CopyField, CopyIconButton } from './CopyField';
import { QaFinalizeControl } from './QaFinalizeControl';
import { SpeiLogo } from './SpeiLogo';
import {
  Root,
  Scroll,
  LogoRow,
  Title,
  Lead,
  AmountBlock,
  AmountLabels,
  AmountLabel,
  AmountValue,
  Panel,
  Notice,
  NoticeIcon,
  NoticeText,
  Expiry,
  StepsTitle,
  StepsList,
  StepRow,
  StepIndex,
  StepText,
  Footer,
  ContinueButton,
  ContinueLabel,
} from './SpeiInstructions.styles';

export type SpeiInstructionsProps = {
  details: SpeiTransferDetails;
  /** Amount due today from the wizard's billing, used when the provider didn't echo one. */
  fallbackAmount: number;
  /** Currency of {@link fallbackAmount}. */
  fallbackCurrency: string;
  /** The buyer confirms they have made the transfer. */
  onContinue: () => void;
  /** True while that confirmation is being registered with the backend. */
  continuing?: boolean;
  /**
   * TEST-ONLY: the commerce-gate `test_finalize_payment_url`. When a non-empty string, a floating
   * QA control is shown that opens it to simulate the transfer settling. `null`/absent in
   * production and on any invoice the backend did not attach one to, so the control never renders
   * for a real buyer. See `QaFinalizeControl` and `data/qa-finalize`.
   */
  qaFinalizeUrl?: string | null;
};

const ClockGlyph = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="9" stroke={SPEI.noticeIcon} strokeWidth="1.6" />
    <path d="M12 7.5V12l3 2" stroke={SPEI.noticeIcon} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * A CLABE is 18 digits; banks print it in 4-4-4-6 groups. Spacing it the same way makes it
 * checkable at a glance — the clipboard still gets the bare digits (see `CopyField.copyValue`).
 * Anything that isn't a plain 18-digit string is left exactly as received.
 */
export function formatClabe(clabe: string): string {
  const digits = clabe.replace(/\s/g, '');
  if (!/^\d{18}$/.test(digits)) return clabe;
  return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8, 12)} ${digits.slice(12)}`;
}

/**
 * The app's locale codes are folder names (`src/locales/{en,mx}`), NOT BCP-47 tags, so they
 * cannot be handed to `Intl` directly: `toLocaleString('mx')` doesn't mean Spanish (Mexico) —
 * `mx` is a country, not a language — and silently falls back to English, which printed
 * "August 5" in the middle of otherwise-Spanish copy. Map the ones we ship; anything else
 * resolves to `undefined`, i.e. the browser's own locale (the convention elsewhere in nova).
 */
const BCP47_BY_APP_LANGUAGE: Record<string, string> = { mx: 'es-MX', es: 'es-MX', en: 'en-US' };

export const dateLocale = (appLanguage: string): string | undefined =>
  BCP47_BY_APP_LANGUAGE[(appLanguage || '').split('-')[0].toLowerCase()];

/**
 * Render an ISO timestamp as a local date+time in the app's language, or `null` if it isn't
 * parseable — an expiry we can't read is better omitted than shown as "Invalid Date".
 */
export function formatExpiry(iso: string, appLanguage: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(dateLocale(appLanguage), {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    // 24-hour on purpose: it is unambiguous for a payment deadline, and it avoids Spanish's
    // "09:00 p.m." colliding with the sentence's own full stop ("…p.m..").
    hour12: false,
  });
}

/**
 * The SPEI coordinates screen: the exact amount, the account the transfer goes to, and a
 * copy button on every value (these get retyped into a banking app, where one wrong digit
 * misroutes the money).
 *
 * Every row is conditional. The provider integration is the source of these values and a
 * partial payload is possible, so a missing field simply drops its row rather than rendering
 * an empty or "undefined" coordinate. The CLABE is the one field the caller guarantees is
 * present (`hasPayableDetails`) before showing this screen at all.
 */
export const SpeiInstructions = ({
  details,
  fallbackAmount,
  fallbackCurrency,
  onContinue,
  continuing = false,
  qaFinalizeUrl,
}: SpeiInstructionsProps): React.ReactElement => {
  const { t, i18n } = useTranslation();

  // Prefer the amount the provider actually expects; fall back to the wizard's own total so
  // the buyer always sees a figure to transfer.
  const amount = details.amount ?? fallbackAmount;
  const currency = details.currency ?? fallbackCurrency;
  const amountText = `${currencySymbol(currency)}${formatAmountWithCode(amount, currency)}`;
  const expiry = details.expiresAt ? formatExpiry(details.expiresAt, i18n.language || 'en') : null;

  return (
    <Root>
      <Scroll>
        <LogoRow>
          <SpeiLogo height={18} />
        </LogoRow>
        <Title text="Transfer these details from your bank" tag="paragraph" />
        <Lead
          text="Open your banking app, add the account below as a new SPEI recipient, and send the exact amount. Your brand is reserved while we wait for the transfer."
          tag="paragraph"
        />

        {/* The amount leads, with its own copy button, rather than being repeated as another
            row in the panel: it is the one figure that must match to the cent, and a mistyped
            amount is the commonest reason a SPEI transfer isn't auto-matched to an order.
            The clipboard gets the bare number — a banking app's amount field rejects "$" and
            the currency code. */}
        <AmountBlock>
          <AmountLabels>
            <AmountLabel text="Amount to transfer" tag="span" />
            <AmountValue text={amountText} tag="span" />
          </AmountLabels>
          <CopyIconButton label={t('Amount')} value={formatAmount(amount)} gaId="amount" />
        </AmountBlock>

        <Panel>
          {details.clabe ? (
            <CopyField
              label={t('CLABE')}
              value={details.clabe.replace(/\s/g, '')}
              display={formatClabe(details.clabe)}
              gaId="clabe"
            />
          ) : null}
          {details.reference ? (
            <CopyField label={t('Reference')} value={details.reference} gaId="reference" />
          ) : null}
          {/* The provider returns `description` separately from `reference`; Mexican banking
              forms ask for both, under "Concepto de pago". */}
          {details.description ? (
            <CopyField label={t('Payment concept')} value={details.description} gaId="concept" />
          ) : null}
          {details.beneficiary ? (
            <CopyField label={t('Beneficiary')} value={details.beneficiary} gaId="beneficiary" />
          ) : null}
          {details.bank ? <CopyField label={t('Bank')} value={details.bank} gaId="bank" /> : null}
        </Panel>

        <Notice>
          <NoticeIcon>{ClockGlyph}</NoticeIcon>
          <NoticeText
            text="SPEI transfers usually arrive within minutes, and can take up to one business day. We'll email you the moment yours lands."
            tag="paragraph"
          />
        </Notice>

        {expiry ? (
          <Expiry text="These details are valid until {{date}}." tag="paragraph" placeholders={{ date: expiry }} />
        ) : null}

        <StepsTitle text="How to pay" tag="paragraph" />
        <StepsList>
          <StepRow>
            <StepIndex>1</StepIndex>
            <StepText text="Open your bank's app or website and choose a SPEI transfer." tag="span" />
          </StepRow>
          <StepRow>
            <StepIndex>2</StepIndex>
            <StepText text="Add the CLABE above as the destination account." tag="span" />
          </StepRow>
          <StepRow>
            <StepIndex>3</StepIndex>
            <StepText text="Send the exact amount, quoting the reference and concept above." tag="span" />
          </StepRow>
        </StepsList>
      </Scroll>

      <Footer>
        <ContinueButton
          type="button"
          data-ga-id="cfg-mx-spei-done"
          onClick={() => {
            // Self-gate: `disabled` styling alone must not be the only guard against a
            // double-acknowledge, which is a real state transition on the backend.
            if (continuing) return;
            onContinue();
          }}
          disabled={continuing}
        >
          <ContinueLabel text={continuing ? t('Confirming…') : t("I've made the transfer")} tag="span" />
        </ContinueButton>
      </Footer>

      {/* TEST-ONLY: a floating QA control that opens the commerce-gate settlement back-door.
          Present only when a non-production invoice actually carried a URL, so it never renders
          for a real buyer. Kept out of `SpeiInstructions` render-tree logic (it reads redux). */}
      {qaFinalizeUrl ? <QaFinalizeControl url={qaFinalizeUrl} /> : null}
    </Root>
  );
};

export default SpeiInstructions;
